import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import logo from "../traderhub24-logo.svg";
import { useAuth } from './contexts/AuthContext'
import { useMembership } from './hooks/useMembership'
import { useNotification } from './contexts/NotificationContext'
import { supabase } from './lib/supabase'
import Login from './components/Login'
import Register from './components/Register'
import ImportTrades from './components/ImportTrades'
import QuickAddTradeModal from './components/QuickAddTradeModal'
import Footer from './components/Footer'
import * as XLSX from 'xlsx'
import { calcPositionRisk, calcPositionRiskPercent } from './lib/tradeCalculations'

interface Trade {
  id: string
  market: 'Commodity' | 'Crypto' | 'Forex' | 'Stock'
  symbol: string
  exchange?: string | null
  trade_type: 'spot' | 'future'
  long_short: 'long' | 'short'
  quantity: number | null
  contract_size: number | null
  entry_date: string | null
  entry_price: number
  stop_loss: number
  take_profit: number
  rr: number | null
  exit_date: string | null
  exit_price: number | null
  trade_setup: string | null
  notes: string | null
  strategy: string | null
  strategy_id: string | null
  currency: string | null
  position_size: number | null
  pnl: number | null
  pnl_percentage: number | null
  total_entry_amount?: number | null
  total_exit_amount?: number | null
  entry_setup_image: string | null
  exit_setup_image: string | null
  time_interval: string | null
  is_public?: boolean | null
  current_price?: number | null
  current_price_updated_at?: string | null
  previous_close_price?: number | null
  unrealized_pnl?: number | null
  unrealized_pnl_percentage?: number | null
  daily_change_percentage?: number | null
  created_at: string
  updated_at: string
  fee?: number | null
  tags?: string[] | null
  account_id?: string | null
  journal_confidence?: number | null
}

interface Strategy {
  id: string
  display_name: string
}

interface Account {
  id: string
  platform: string
  currency: string
}

function AllTrades({ onBack, setCurrentPage, embedded }: { onBack: () => void; setCurrentPage: (page: "home" | "changelog" | "all-trades" | "analytics" | "tools" | "strategies" | "social-trade" | "account" | "community") => void; embedded?: boolean }) {
  const { t } = useTranslation(['trading', 'common'])
  const { user, signOut, loading: authLoading } = useAuth()
  const { membershipType, profileLoading } = useMembership()
  const { showError, showWarning, showConfirm } = useNotification()
  const navigate = useNavigate()
  const [showLogin, setShowLogin] = useState(false)
  const [showRegister, setShowRegister] = useState(false)
  const [trades, setTrades] = useState<Trade[]>([])
  const [strategies, setStrategies] = useState<Strategy[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [showImport, setShowImport] = useState(false)
  const [editingTrade, setEditingTrade] = useState<Trade | null>(null)
  const [tradeListRefresh, setTradeListRefresh] = useState(0)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // Filter persistence - survives navigation when user views/edits a trade and returns
  const FILTER_STORAGE_KEY = 'allTrades_filters'
  type FilterQuickDate = 'all' | 'd' | 'w' | 'm' | '6m' | 'ytd' | 'Y'
  type FilterRrType = 'none' | 'greater' | 'less' | 'between'
  interface PersistedFilterState {
    filterMarket: string
    filterExchange: string
    filterLongShort: string
    filterStatus: string
    filterSymbol: string
    filterStrategy: string
    filterEntryDateFrom: string
    filterEntryDateTo: string
    filterExitDateFrom: string
    filterExitDateTo: string
    filterQuickDate: FilterQuickDate
    filterTimeInterval: string
    filterAccount: string
    filterRrType: FilterRrType
    filterRrValue: string
    filterRrValue2: string
    filterPnlPercentType: FilterRrType
    filterPnlPercentValue: string
    filterPnlPercentValue2: string
    showOnlyOpen: boolean
  }
  const DEFAULT_FILTER_STATE: PersistedFilterState = {
    filterMarket: 'all',
    filterExchange: 'all',
    filterLongShort: 'all',
    filterStatus: 'all',
    filterSymbol: '',
    filterStrategy: 'all',
    filterEntryDateFrom: '',
    filterEntryDateTo: '',
    filterExitDateFrom: '',
    filterExitDateTo: '',
    filterQuickDate: 'all',
    filterTimeInterval: 'all',
    filterAccount: 'all',
    filterRrType: 'none',
    filterRrValue: '',
    filterRrValue2: '',
    filterPnlPercentType: 'none',
    filterPnlPercentValue: '',
    filterPnlPercentValue2: '',
    showOnlyOpen: false,
  }
  function loadFilterState(): PersistedFilterState {
    try {
      const raw = localStorage.getItem(FILTER_STORAGE_KEY)
      if (!raw) return DEFAULT_FILTER_STATE
      const parsed = JSON.parse(raw) as unknown
      if (!parsed || typeof parsed !== 'object') return DEFAULT_FILTER_STATE
      return { ...DEFAULT_FILTER_STATE, ...parsed }
    } catch {
      return DEFAULT_FILTER_STATE
    }
  }
  function saveFilterState(state: PersistedFilterState) {
    try {
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(state))
    } catch (_) {}
  }

  // Filter states - persisted in localStorage so filters survive navigation (e.g. after viewing/editing a trade)
  const initialFilters = loadFilterState()
  const [filterMarket, setFilterMarket] = useState<string>(initialFilters.filterMarket)
  const [filterExchange, setFilterExchange] = useState<string>(initialFilters.filterExchange)
  const [filterLongShort, setFilterLongShort] = useState<string>(initialFilters.filterLongShort)
  const [filterStatus, setFilterStatus] = useState<string>(initialFilters.filterStatus)
  const [filterSymbol, setFilterSymbol] = useState<string>(initialFilters.filterSymbol)
  const [filterStrategy, setFilterStrategy] = useState<string>(initialFilters.filterStrategy)
  const [filterEntryDateFrom, setFilterEntryDateFrom] = useState<string>(initialFilters.filterEntryDateFrom)
  const [filterEntryDateTo, setFilterEntryDateTo] = useState<string>(initialFilters.filterEntryDateTo)
  const [filterExitDateFrom, setFilterExitDateFrom] = useState<string>(initialFilters.filterExitDateFrom)
  const [filterExitDateTo, setFilterExitDateTo] = useState<string>(initialFilters.filterExitDateTo)
  const [filterQuickDate, setFilterQuickDate] = useState<FilterQuickDate>(initialFilters.filterQuickDate)
  const [filterTimeInterval, setFilterTimeInterval] = useState<string>(initialFilters.filterTimeInterval)
  const [filterAccount, setFilterAccount] = useState<string>(initialFilters.filterAccount)
  const [filterRrType, setFilterRrType] = useState<FilterRrType>(initialFilters.filterRrType)
  const [filterRrValue, setFilterRrValue] = useState<string>(initialFilters.filterRrValue)
  const [filterRrValue2, setFilterRrValue2] = useState<string>(initialFilters.filterRrValue2)
  const [filterPnlPercentType, setFilterPnlPercentType] = useState<FilterRrType>(initialFilters.filterPnlPercentType)
  const [filterPnlPercentValue, setFilterPnlPercentValue] = useState<string>(initialFilters.filterPnlPercentValue)
  const [filterPnlPercentValue2, setFilterPnlPercentValue2] = useState<string>(initialFilters.filterPnlPercentValue2)
  const [showFilters, setShowFilters] = useState(false)
  const [showOnlyOpen, setShowOnlyOpen] = useState<boolean>(initialFilters.showOnlyOpen)
  
  // Table columns (like Trade Journal): visible columns order, persisted in localStorage
  const ALL_TABLE_COLUMNS = ['market', 'exchange', 'entry_date', 'entry_price', 'exit_date', 'exit_price', 'position_size', 'total_invested', 'average_cost', 'rr', 'sl', 'tp', 'risk', 'risk_percent', 'pnl', 'pnl_percent', 'fee', 'tags', 'account'] as const
  type TableColumnId = typeof ALL_TABLE_COLUMNS[number]

  // Sort state (can be Trade key or table column id like pnl_percent, average_cost)
  const [sortField, setSortField] = useState<keyof Trade | TableColumnId | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const TABLE_STORAGE_KEY = 'allTrades_visibleColumns'
  function loadVisibleColumns(): TableColumnId[] {
    try {
      const raw = localStorage.getItem(TABLE_STORAGE_KEY)
      if (!raw) return ['entry_date', 'entry_price', 'exit_date', 'exit_price', 'position_size', 'pnl', 'pnl_percent']
      const parsed = JSON.parse(raw) as unknown
      if (!Array.isArray(parsed)) return ['entry_date', 'entry_price', 'exit_date', 'exit_price', 'position_size', 'pnl', 'pnl_percent']
      const valid = parsed.filter((id): id is TableColumnId => ALL_TABLE_COLUMNS.includes(id as TableColumnId))
      return valid.length > 0 ? valid : ['entry_date', 'entry_price', 'exit_date', 'exit_price', 'position_size', 'pnl', 'pnl_percent']
    } catch {
      return ['entry_date', 'entry_price', 'exit_date', 'exit_price', 'position_size', 'pnl', 'pnl_percent']
    }
  }
  const [visibleColumns, setVisibleColumns] = useState<TableColumnId[]>(() => loadVisibleColumns())
  const [showEditTableModal, setShowEditTableModal] = useState(false)
  const [editTableDraft, setEditTableDraft] = useState<TableColumnId[] | null>(null)

  // Session control - redirect to login if not authenticated (skip when embedded; layout handles auth)
  useEffect(() => {
    if (!embedded && !authLoading && !user) {
      setShowLogin(true)
    }
  }, [embedded, authLoading, user])

  const fetchTrades = async () => {
    if (!user) {
      setTrades([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('trades_with_market_data')
        .select('*')
        .eq('user_id', user.id)
        .order('entry_date', { ascending: false })

      if (error) throw error

      const baseTrades = (data || []) as Trade[]

      // Hydrate `exchange` from `symbols.exchange` (best-effort lookup by symbol + market).
      // Note: trades table stores symbol as text, so we enrich client-side.
      try {
        const uniqueSymbols = Array.from(
          new Set(baseTrades.map((t) => t.symbol).filter(Boolean))
        )
        const uniqueMarkets = Array.from(
          new Set(baseTrades.map((t) => t.market).filter(Boolean))
        )

        if (uniqueSymbols.length > 0) {
          let symbolsQuery = supabase
            .from('symbols')
            .select('symbol, market, exchange')
            .in('symbol', uniqueSymbols)

          if (uniqueMarkets.length > 0) {
            symbolsQuery = symbolsQuery.in('market', uniqueMarkets)
          }

          const { data: symbolsData, error: symbolsError } = await symbolsQuery

          if (!symbolsError && symbolsData) {
            const exchangeByKey = new Map<string, string | null>()
            for (const s of symbolsData as Array<{ symbol: string; market: string; exchange: string | null }>) {
              const key = `${s.market}::${s.symbol}`.toUpperCase()
              // Prefer first non-null exchange we see.
              if (!exchangeByKey.has(key) || (exchangeByKey.get(key) == null && s.exchange != null)) {
                exchangeByKey.set(key, s.exchange)
              }
            }

            const hydrated = baseTrades.map((t) => {
              const key = `${t.market}::${t.symbol}`.toUpperCase()
              return { ...t, exchange: exchangeByKey.get(key) ?? null }
            })
            setTrades(hydrated)
          } else {
            setTrades(baseTrades)
          }
        } else {
          setTrades(baseTrades)
        }
      } catch (hydrateErr) {
        console.warn('Failed to hydrate exchanges from symbols:', hydrateErr)
        setTrades(baseTrades)
      }
      setError(null)
    } catch (err: any) {
      setError(err.message || 'Failed to load trades')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user) {
      fetchTrades()
      fetchStrategies()
      fetchAccounts()
    }
  }, [user, tradeListRefresh])

  // Persist filter state to localStorage when filters change (so filters survive navigation)
  useEffect(() => {
    saveFilterState({
      filterMarket,
      filterExchange,
      filterLongShort,
      filterStatus,
      filterSymbol,
      filterStrategy,
      filterEntryDateFrom,
      filterEntryDateTo,
      filterExitDateFrom,
      filterExitDateTo,
      filterQuickDate,
      filterTimeInterval,
      filterAccount,
      filterRrType,
      filterRrValue,
      filterRrValue2,
      filterPnlPercentType,
      filterPnlPercentValue,
      filterPnlPercentValue2,
      showOnlyOpen,
    })
  }, [
    filterMarket,
    filterExchange,
    filterLongShort,
    filterStatus,
    filterSymbol,
    filterStrategy,
    filterEntryDateFrom,
    filterEntryDateTo,
    filterExitDateFrom,
    filterExitDateTo,
    filterQuickDate,
    filterTimeInterval,
    filterAccount,
    filterRrType,
    filterRrValue,
    filterRrValue2,
    filterPnlPercentType,
    filterPnlPercentValue,
    filterPnlPercentValue2,
    showOnlyOpen,
  ])

  const fetchStrategies = async () => {
    if (!user) {
      setStrategies([])
      return
    }

    try {
      const { data, error } = await supabase
        .from('strategies')
        .select('id, display_name')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('display_name', { ascending: true })

      if (error) throw error

      setStrategies(data || [])
    } catch (err: any) {
      console.error('Failed to load strategies:', err)
    }
  }

  const fetchAccounts = async () => {
    if (!user) {
      setAccounts([])
      return
    }
    try {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, platform, currency')
        .eq('user_id', user.id)
        .order('platform', { ascending: true })
      if (error) throw error
      setAccounts(data || [])
    } catch (err: any) {
      console.error('Failed to load accounts:', err)
      setAccounts([])
    }
  }

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const confirmed = await showConfirm({
      title: t('tradeList.delete'),
      message: t('tradeList.deleteConfirm'),
      confirmText: t('tradeList.delete'),
      cancelText: t('buttons.cancel', { ns: 'common' }),
      confirmButtonStyle: 'danger'
    })
    if (!confirmed) {
      return
    }

    try {
      setDeletingId(id)
      const { error } = await supabase
        .from('trades')
        .delete()
        .eq('id', id)

      if (error) throw error

      await fetchTrades()
    } catch (err: any) {
      showError(err.message || 'Failed to delete trade')
    } finally {
      setDeletingId(null)
    }
  }

  const formatPriceWithCurrency = (price: number | null, currency: string | null) => {
    if (price === null) return '-'
    const formatted = price.toFixed(2).replace('.', ',')
    return currency ? `${formatted} ${currency}` : formatted
  }

  const formatCurrency = (amount: number | null, currency: string | null) => {
    if (amount === null) return '-'
    const formatted = amount.toFixed(2).replace('.', ',')
    return currency ? `${formatted} ${currency}` : formatted
  }

  const getAverageCost = (trade: Trade): number | null => {
    const qty = trade.quantity ?? 0
    if (qty <= 0) return trade.entry_price
    const basis = (trade as Trade & { cost_basis_remaining?: number | null }).cost_basis_remaining ??
      (trade.long_short === 'long' ? trade.total_entry_amount : trade.total_exit_amount)
    if (basis != null && basis > 0) return basis / qty
    return trade.entry_price
  }

  const getTotalInvested = (trade: Trade): number | null => {
    const invested = trade.total_entry_amount ?? trade.position_size
    if (invested != null && invested > 0) return invested
    const qty = trade.quantity ?? 0
    const entry = trade.entry_price
    if (qty > 0 && entry > 0) return qty * entry
    return null
  }

  const getPositionRisk = (trade: Trade): number | null =>
    calcPositionRisk(trade.entry_price, trade.stop_loss, trade.quantity, trade.contract_size, trade.long_short)

  const getPositionRiskPercent = (trade: Trade): number | null =>
    calcPositionRiskPercent(
      trade.entry_price,
      trade.stop_loss,
      trade.quantity,
      trade.contract_size,
      trade.long_short,
      getTotalInvested(trade),
    )

  const getTableColumnLabel = (col: TableColumnId): string => {
    switch (col) {
      case 'market': return t('allTrades.columnMarket')
      case 'exchange': return t('allTrades.columnExchange')
      case 'entry_date': return t('allTrades.columnEntry')
      case 'entry_price': return t('allTrades.columnEntryPrice')
      case 'exit_date': return t('allTrades.columnExit')
      case 'exit_price': return t('allTrades.columnExitPrice')
      case 'position_size': return t('allTrades.columnPositionSize')
      case 'total_invested': return t('allTrades.columnTotalInvested')
      case 'average_cost': return t('allTrades.columnAverageCost')
      case 'rr': return t('allTrades.columnRr')
      case 'sl': return t('allTrades.columnSl')
      case 'tp': return t('allTrades.columnTp')
      case 'risk': return t('allTrades.columnRisk')
      case 'risk_percent': return t('allTrades.columnRiskPercent')
      case 'pnl': return t('allTrades.columnPnl')
      case 'pnl_percent': return t('allTrades.columnPnlPercent')
      case 'fee': return t('allTrades.columnFee')
      case 'tags': return t('allTrades.columnTags')
      case 'account': return t('allTrades.columnAccount')
      default: return ''
    }
  }

  const createDetailId = (trade: Trade) => {
    const uuid = crypto.randomUUID ? crypto.randomUUID() : 
      `detail-${Math.random().toString(36).slice(2, 10)}`
    return `${trade.symbol}-${uuid}`
  }

  const seededAllowHistory = (seed: string) => {
    let hash = 0
    for (let i = 0; i < seed.length; i++) {
      hash = (hash << 5) - hash + seed.charCodeAt(i)
      hash |= 0
    }
    const ratio = (hash >>> 0) / 4294967295
    return ratio < 0.2
  }

  const buildDetailState = (trade: Trade) => {
    const { id: _tradeId, ...restWithUser } = trade as Trade & { user_id?: string }
    const { user_id: _userId, ...tradePayload } = restWithUser

    const baseDate = trade.entry_date ? new Date(trade.entry_date) : new Date()
    const entryPrice = trade.entry_price
    const exitPrice =
      trade.exit_price ??
      (trade.long_short === 'long' ? entryPrice * 1.02 : entryPrice * 0.98)

    const timeline = [
      {
        date: baseDate.toISOString(),
        title: 'Trade Opened',
        description: 'Initial entry placed; alert triggered on setup validation.',
        price: entryPrice,
        tag: 'entry',
      },
      {
        date: new Date(baseDate.getTime() + 24 * 60 * 60 * 1000).toISOString(),
        title: 'Daily Review',
        description: 'Reviewed stop placement and kept risk constant.',
        price: entryPrice * (trade.long_short === 'long' ? 1.01 : 0.99),
        tag: 'review',
      },
      {
        date: new Date(baseDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString(),
        title: 'Market Event',
        description: 'Macro headline created volatility; kept discipline.',
        tag: 'macro',
      },
      {
        date: new Date(baseDate.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString(),
        title: 'Adjustment',
        description: 'Moved stop to breakeven after partial target touched.',
        price: entryPrice,
        tag: 'risk',
      },
      {
        date: new Date(baseDate.getTime() + 4 * 24 * 60 * 60 * 1000).toISOString(),
        title: trade.exit_date ? 'Trade Closed' : 'Current Status',
        description: trade.exit_date
          ? 'Position exited; captured planned move.'
          : 'Position still active; monitoring key levels.',
        price: exitPrice,
        tag: trade.exit_date ? 'exit' : 'open',
      },
    ]

    const comments = [
      {
        author: 'Self-Note',
        text: 'Keep journaling intraday emotions; avoid revenge trades.',
        date: baseDate.toISOString(),
        mood: 'Focused',
      },
      {
        author: 'Market',
        text: 'Watch for liquidity sweep near prior highs before adding size.',
        date: new Date(baseDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString(),
        mood: 'Alert',
      },
    ]

    return {
      trade: tradePayload,
      timeline,
      comments,
      mood: 'Focused',
      tradeId: trade.id,
    }
  }

  const handleNavigateToDetail = (trade: Trade) => {
    const detailId = createDetailId(trade)
    const detailState = buildDetailState(trade)
    const historyAccessGranted =
      membershipType === 'pro'
        ? true
        : membershipType === 'free'
          ? seededAllowHistory(detailId)
          : false

    navigate(`/app/trade/${detailId}`, { state: { ...detailState, historyAccessGranted } })
  }

  const isTradeOpen = (trade: Trade) => {
    return !trade.exit_date
  }

  // Filter and sort trades
  const getFilteredAndSortedTrades = () => {
    let filtered = [...trades]

    const canonicalizeExchange = (raw: string | null | undefined): string | null => {
      if (!raw) return null
      const trimmed = raw.trim()
      if (!trimmed) return null
      const upper = trimmed.toUpperCase()
      const upperNorm = upper.replace(/İ/g, 'I') // Turkish İ -> I for comparison

      // Group US exchanges under one option (including MIC codes XNAS, XNYS)
      const isNasdaq =
        upper === 'NASDAQ' ||
        upper === 'XNAS' ||
        upper.includes('NASDAQ STOCK') ||
        upper.includes('NASDAQ GLOBAL') ||
        upper.includes('NASDAQ CAPITAL') ||
        upper.includes('NASDAQ GS') ||
        upper.includes('NASDAQ GM')
      const isNyse =
        upper === 'NYSE' ||
        upper === 'XNYS' ||
        upper.includes('NEW YORK STOCK EXCHANGE') ||
        upper.includes('NYSE AMERICAN') ||
        upper.includes('NYSE ARCA')

      if (isNasdaq || isNyse) return 'US Exchanges'

      // BIST (Borsa Istanbul)
      const isBist =
        upper === 'BIST' ||
        upperNorm.includes('BORSA ISTANBUL') ||
        upper === 'XIST'

      if (isBist) return 'BIST'

      return 'Other'
    }

    // Apply filters
    if (filterMarket !== 'all') {
      filtered = filtered.filter(trade => trade.market === filterMarket)
    }

    if (filterExchange !== 'all') {
      filtered = filtered.filter((trade) => {
        const canon = canonicalizeExchange(trade.exchange)
        if (filterExchange === 'Other') {
          return canon === 'Other' || canon === null
        }
        return (canon || '').toLowerCase() === filterExchange.toLowerCase()
      })
    }

    if (filterLongShort !== 'all') {
      filtered = filtered.filter(trade => trade.long_short === filterLongShort)
    }

    if (filterStatus === 'open') {
      filtered = filtered.filter(trade => isTradeOpen(trade))
    } else if (filterStatus === 'closed') {
      filtered = filtered.filter(trade => !isTradeOpen(trade))
    }

    // Quick filter: show only open trades (like Trade Journal)
    if (showOnlyOpen) {
      filtered = filtered.filter(trade => isTradeOpen(trade))
    }

    if (filterSymbol) {
      filtered = filtered.filter(trade => 
        trade.symbol.toLowerCase().includes(filterSymbol.toLowerCase())
      )
    }

    if (filterStrategy !== 'all') {
      if (filterStrategy === '__none__') {
        filtered = filtered.filter((trade) => {
          const hasStrategyId = !!trade.strategy_id
          const legacyStrategyText = (trade.strategy || '').trim()
          return !hasStrategyId && legacyStrategyText.length === 0
        })
      } else {
        filtered = filtered.filter(trade => trade.strategy_id === filterStrategy)
      }
    }

    // Quick date filter (entry date based)
    if (filterQuickDate !== 'all') {
      const now = new Date()
      let from: Date
      let to: Date = new Date(now)
      to.setHours(23, 59, 59, 999)
      if (filterQuickDate === 'd') {
        from = new Date(now)
        from.setHours(0, 0, 0, 0)
      } else if (filterQuickDate === 'w') {
        from = new Date(now)
        from.setDate(from.getDate() - 7)
        from.setHours(0, 0, 0, 0)
      } else if (filterQuickDate === 'm') {
        from = new Date(now)
        from.setMonth(from.getMonth() - 1)
        from.setHours(0, 0, 0, 0)
      } else if (filterQuickDate === '6m') {
        from = new Date(now)
        from.setMonth(from.getMonth() - 6)
        from.setHours(0, 0, 0, 0)
      } else if (filterQuickDate === 'ytd') {
        from = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0)
      } else if (filterQuickDate === 'Y') {
        from = new Date(now)
        from.setFullYear(from.getFullYear() - 1)
        from.setHours(0, 0, 0, 0)
      } else {
        from = new Date(0)
      }
      filtered = filtered.filter(trade => {
        if (!trade.entry_date) return false
        const d = new Date(trade.entry_date)
        return d >= from && d <= to
      })
    }

    // Entry date range
    if (filterEntryDateFrom) {
      filtered = filtered.filter(trade => {
        if (!trade.entry_date) return false
        return new Date(trade.entry_date) >= new Date(filterEntryDateFrom)
      })
    }
    if (filterEntryDateTo) {
      filtered = filtered.filter(trade => {
        if (!trade.entry_date) return false
        const tradeDate = new Date(trade.entry_date)
        const toDate = new Date(filterEntryDateTo)
        toDate.setHours(23, 59, 59, 999)
        return tradeDate <= toDate
      })
    }

    // Exit date range
    if (filterExitDateFrom) {
      filtered = filtered.filter(trade => {
        if (!trade.exit_date) return false
        return new Date(trade.exit_date) >= new Date(filterExitDateFrom)
      })
    }
    if (filterExitDateTo) {
      filtered = filtered.filter(trade => {
        if (!trade.exit_date) return false
        const tradeDate = new Date(trade.exit_date)
        const toDate = new Date(filterExitDateTo)
        toDate.setHours(23, 59, 59, 999)
        return tradeDate <= toDate
      })
    }

    if (filterAccount !== 'all') {
      if (filterAccount === '__none__') {
        filtered = filtered.filter((trade) => !trade.account_id)
      } else {
        filtered = filtered.filter((trade) => trade.account_id === filterAccount)
      }
    }

    // Time Interval filter
    if (filterTimeInterval !== 'all') {
      if (filterTimeInterval === '__none__') {
        filtered = filtered.filter((trade) => !trade.time_interval || trade.time_interval.trim().length === 0)
      } else {
        filtered = filtered.filter(trade => trade.time_interval === filterTimeInterval)
      }
    }

    // RR filter
    if (filterRrType !== 'none') {
      const rrValue = parseFloat(filterRrValue)
      if (!isNaN(rrValue)) {
        if (filterRrType === 'greater') {
          filtered = filtered.filter(trade => trade.rr !== null && trade.rr > rrValue)
        } else if (filterRrType === 'less') {
          filtered = filtered.filter(trade => trade.rr !== null && trade.rr < rrValue)
        } else if (filterRrType === 'between') {
          const rrValue2 = parseFloat(filterRrValue2)
          if (!isNaN(rrValue2)) {
            const min = Math.min(rrValue, rrValue2)
            const max = Math.max(rrValue, rrValue2)
            filtered = filtered.filter(trade => trade.rr !== null && trade.rr >= min && trade.rr <= max)
          }
        }
      }
    }

    // P&L % filter
    if (filterPnlPercentType !== 'none') {
      const pnlPercentValue = parseFloat(filterPnlPercentValue)
      if (!isNaN(pnlPercentValue)) {
        if (filterPnlPercentType === 'greater') {
          filtered = filtered.filter(trade => trade.pnl_percentage !== null && trade.pnl_percentage > pnlPercentValue)
        } else if (filterPnlPercentType === 'less') {
          filtered = filtered.filter(trade => trade.pnl_percentage !== null && trade.pnl_percentage < pnlPercentValue)
        } else if (filterPnlPercentType === 'between') {
          const pnlPercentValue2 = parseFloat(filterPnlPercentValue2)
          if (!isNaN(pnlPercentValue2)) {
            const min = Math.min(pnlPercentValue, pnlPercentValue2)
            const max = Math.max(pnlPercentValue, pnlPercentValue2)
            filtered = filtered.filter(trade => trade.pnl_percentage !== null && trade.pnl_percentage >= min && trade.pnl_percentage <= max)
          }
        }
      }
    }

    // Apply sorting
    if (sortField) {
      const getSortValue = (trade: Trade): string | number | null => {
        const key = sortField as keyof Trade
        if (sortField === 'average_cost') return getAverageCost(trade)
        if (sortField === 'total_invested') return getTotalInvested(trade)
        if (sortField === 'risk') return getPositionRisk(trade)
        if (sortField === 'risk_percent') return getPositionRiskPercent(trade)
        if (sortField === 'pnl') {
          const open = !trade.exit_date
          return open ? (trade.unrealized_pnl ?? null) : (trade.pnl ?? null)
        }
        if (sortField === 'pnl_percent') {
          const open = !trade.exit_date
          return open ? (trade.unrealized_pnl_percentage ?? null) : (trade.pnl_percentage ?? null)
        }
        if (sortField === 'fee') return trade.fee ?? null
        if (sortField === 'tags') return Array.isArray(trade.tags) && trade.tags.length > 0 ? trade.tags.join(', ') : ''
        if (sortField === 'account') {
          const acc = trade.account_id ? accounts.find(a => a.id === trade.account_id) : null
          return acc ? `${acc.platform} (${acc.currency})` : ''
        }
        if (key in trade) {
          const v = (trade as unknown as Record<string, unknown>)[key]
          return v as string | number | null
        }
        return null
      }
      filtered.sort((a, b) => {
        const aValue = getSortValue(a)
        const bValue = getSortValue(b)
        if (aValue === null && bValue === null) return 0
        if (aValue === null) return 1
        if (bValue === null) return -1
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
        }
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
        }
        return 0
      })
    }

    return filtered
  }

  const handleSort = (field: keyof Trade | TableColumnId) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const clearFilters = () => {
    setFilterMarket('all')
    setFilterExchange('all')
    setFilterLongShort('all')
    setFilterStatus('all')
    setFilterSymbol('')
    setFilterStrategy('all')
    setFilterEntryDateFrom('')
    setFilterEntryDateTo('')
    setFilterExitDateFrom('')
    setFilterExitDateTo('')
    setFilterQuickDate('all')
    setFilterTimeInterval('all')
    setFilterAccount('all')
    setFilterRrType('none')
    setFilterRrValue('')
    setFilterRrValue2('')
    setFilterPnlPercentType('none')
    setFilterPnlPercentValue('')
    setFilterPnlPercentValue2('')
  }

  const exportToExcel = () => {
    const filtered = getFilteredAndSortedTrades()
    
    if (filtered.length === 0) {
      showWarning('No trades to export')
      return
    }

    // Prepare data for Excel
    const excelData = filtered.map(trade => {
      const strategyName = strategies.find(s => s.id === trade.strategy_id)?.display_name || trade.strategy || '-'
      const acc = trade.account_id ? accounts.find(a => a.id === trade.account_id) : null
      const accountDisplay = acc ? `${acc.platform} (${acc.currency})` : '-'
      const tagsDisplay = Array.isArray(trade.tags) && trade.tags.length > 0 ? trade.tags.join(', ') : '-'
      const isOpen = isTradeOpen(trade)
      
      return {
        'Symbol': trade.symbol,
        'Market': trade.market,
        'Direction': trade.long_short.toUpperCase(),
        'Entry Date': trade.entry_date ? new Date(trade.entry_date).toLocaleDateString() : '-',
        'Entry Price': trade.entry_price,
        'Exit Date': isOpen ? '-' : (trade.exit_date ? new Date(trade.exit_date).toLocaleDateString() : '-'),
        'Exit Price': isOpen ? '-' : (trade.exit_price || '-'),
        'Stop Loss': trade.stop_loss,
        'Take Profit': trade.take_profit,
        'RR': trade.rr || '-',
        'Quantity': trade.quantity || '-',
        'Position Size': trade.position_size || '-',
        'Total Invested': getTotalInvested(trade) ?? '-',
        'P&L': isOpen ? (trade.unrealized_pnl !== null && trade.unrealized_pnl !== undefined ? trade.unrealized_pnl : '-') : (trade.pnl || '-'),
        'P&L %': isOpen 
          ? (trade.unrealized_pnl_percentage !== null && trade.unrealized_pnl_percentage !== undefined ? `${trade.unrealized_pnl_percentage.toFixed(2)}%` : '-')
          : (trade.pnl_percentage !== null ? `${trade.pnl_percentage.toFixed(2)}%` : '-'),
        'Currency': trade.currency || '-',
        'Fee': trade.fee != null ? trade.fee : '-',
        'Account': accountDisplay,
        'Tags': tagsDisplay,
        'Strategy': strategyName,
        'Time Interval': trade.time_interval || '-',
        'Trade Setup': trade.trade_setup || '-',
        'Status': isOpen ? 'OPEN' : 'CLOSED',
        'Notes': trade.notes || '-'
      }
    })

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Trades')

    // Generate filename with current date
    const date = new Date().toISOString().split('T')[0]
    const filename = `trades_${date}.xls`

    // Write file (XLS format)
    XLSX.writeFile(wb, filename, { bookType: 'xls' })
  }

  const filteredTrades = getFilteredAndSortedTrades()
  const canonicalizeExchangeForOptions = (raw: string | null | undefined): string | null => {
    if (!raw) return null
    const trimmed = raw.trim()
    if (!trimmed) return null
    const upper = trimmed.toUpperCase()
    const upperNorm = upper.replace(/İ/g, 'I')
    const isNasdaq =
      upper === 'NASDAQ' ||
      upper === 'XNAS' ||
      upper.includes('NASDAQ STOCK') ||
      upper.includes('NASDAQ GLOBAL') ||
      upper.includes('NASDAQ CAPITAL') ||
      upper.includes('NASDAQ GS') ||
      upper.includes('NASDAQ GM')
    const isNyse =
      upper === 'NYSE' ||
      upper === 'XNYS' ||
      upper.includes('NEW YORK STOCK EXCHANGE') ||
      upper.includes('NYSE AMERICAN') ||
      upper.includes('NYSE ARCA')
    if (isNasdaq || isNyse) return 'US Exchanges'
    const isBist =
      upper === 'BIST' ||
      upperNorm.includes('BORSA ISTANBUL') ||
      upper === 'XIST'
    if (isBist) return 'BIST'
    return 'Other'
  }

  const availableExchanges = Array.from(
    new Set([
      ...trades
        .map((t) => canonicalizeExchangeForOptions(t.exchange))
        .filter((x): x is string => !!x && x.trim().length > 0),
      'Other' // Always show Other for non-US, non-BIST exchanges
    ])
  ).sort((a, b) => {
    const order = ['US Exchanges', 'BIST', 'Other']
    const ai = order.indexOf(a)
    const bi = order.indexOf(b)
    if (ai >= 0 && bi >= 0) return ai - bi
    if (a === 'US Exchanges' && b !== 'US Exchanges') return -1
    if (b === 'US Exchanges' && a !== 'US Exchanges') return 1
    return a.localeCompare(b)
  })

  // If not authenticated, show login prompt (skip when embedded; layout handles auth)
  if (!user && !authLoading && !embedded) {
    return (
      <div className="min-h-screen bg-app-bg text-gray-900">
        {/* Header */}
        <header className="w-full flex justify-between items-center py-6 px-6 max-w-6xl mx-auto">
          <div className="flex items-center space-x-2">
            <img 
              src={logo} 
              alt="TraderHub24" 
              className="h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
              onClick={onBack}
            />
          </div>
          <div className="flex items-center gap-2">
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-6xl mx-auto px-6 py-6">
          <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-6">
            <p className="text-gray-500 text-center mb-4">Please sign in to view your trades</p>
            <div className="flex justify-center gap-2">
              <button
                onClick={() => setShowLogin(true)}
                className="rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => setShowRegister(true)}
                className="rounded-full bg-orange-500 hover:bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors"
              >
                Sign Up
              </button>
            </div>
          </div>
        </main>

        {/* Footer */}
        <Footer currentPage={undefined} onNavigate={(page) => {
          if (page === "privacy-policy") navigate("/privacy");
          else if (page === "terms-and-conditions") navigate("/terms");
          else if (page === "risk-warning") navigate("/risk-warning");
          else if (page === "documentation") navigate("/documentation");
          else if (page === "contact") navigate("/contact");
          else if (page === "home") onBack();
          else if (page === "changelog") navigate("/changelog");
        }} />

        {/* Auth Modals */}
        {showLogin && (
          <Login
            onClose={() => setShowLogin(false)}
            onSwitchToRegister={() => {
              setShowLogin(false)
              setShowRegister(true)
            }}
          />
        )}
        {showRegister && (
          <Register
            onClose={() => setShowRegister(false)}
            onSwitchToLogin={() => {
              setShowRegister(false)
              setShowLogin(true)
            }}
          />
        )}
      </div>
    )
  }

  return (
    <div className={embedded ? "w-full" : "min-h-screen bg-app-bg text-gray-900"}>
      {!embedded && (
      <header className="w-full py-3 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <img 
            src={logo} 
            alt="TraderHub24" 
            className="h-12 md:h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
            onClick={onBack}
          />
          <div className="flex items-center gap-2">
            {authLoading ? (
              <div className="text-sm text-gray-500">Loading...</div>
            ) : user ? (
              <div className="relative flex items-center gap-2">
                {/* Mobile Menu Button */}
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {showMobileMenu ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                    )}
                  </svg>
                </button>

                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="px-3 md:px-4 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 border border-gray-300"
                    title="Account"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="hidden md:inline">Account</span>
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-64 bg-app-box rounded-lg shadow-lg border border-gray-200 z-50">
                      <div className="p-4 border-b border-gray-200">
                        <p className="text-sm font-semibold text-gray-900">Account</p>
                        <p className="text-sm text-gray-600 mt-1 truncate">{user.email}</p>
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-xs text-gray-500">Membership</span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            membershipType === 'pro'
                              ? 'bg-purple-100 text-purple-700'
                              : membershipType === 'publisher'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {profileLoading ? 'Loading...' : membershipType.toUpperCase()}
                        </span>
                        </div>
                      </div>
                      <div className="p-2">
                        <button
                          onClick={() => {
                            navigate("/app/account")
                            setShowUserMenu(false)
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4a4 4 0 100 8 4 4 0 000-8zM6 20a6 6 0 0112 0" />
                          </svg>
                          Account Settings
                        </button>
                        <button
                          onClick={() => {
                            signOut();
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowLogin(true)}
                className="px-4 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 border border-gray-300"
                title="Sign In"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                Sign In
              </button>
            )}
          </div>
        </div>

        {/* Mobile Sidebar/Menu */}
        {showMobileMenu && user && (
          <div className="md:hidden fixed inset-0 z-[100] flex">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowMobileMenu(false)} />
            <div className="relative w-64 max-w-[80%] h-full bg-app-box shadow-xl flex flex-col p-4 animate-in slide-in-from-left duration-300">
              <div className="flex items-center justify-between mb-8">
                <img src={logo} alt="TraderHub24" className="h-8 w-auto" />
                <button onClick={() => setShowMobileMenu(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <nav className="space-y-2">
                {[
                  { id: "all-trades", label: "All Trades", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" },
                  { id: "analytics", label: "Analytics", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
                  { id: "tools", label: "Tools", icon: "M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a1 1 0 01-1-1v-3a1 1 0 011-1h1a2 2 0 100-4H4a1 1 0 01-1-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" },
                  { id: "strategies", label: "Strategies", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
                  { id: "social-trade", label: "Social Trade", icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" },
                  { id: "community", label: "Community", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setCurrentPage(item.id as any);
                      setShowMobileMenu(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      item.id === "all-trades" 
                        ? "bg-orange-50 text-orange-600" 
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    {item.label}
                  </button>
                ))}
                <button
                  onClick={() => {
                    navigate('/app/add-trade');
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 transition-colors mt-4"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Trade
                </button>
              </nav>
            </div>
          </div>
        )}
      </header>
      )}

      {/* Main Content */}
      {(() => {
        const W = embedded ? 'div' : 'main';
        return (
        <W className={embedded ? "dashboard-page-content" : "max-w-6xl mx-auto px-4 md:px-6 py-6"}>
        {!embedded && (
        <>
        {/* Navigation Links - Desktop Only */}
        <section className="hidden md:flex items-center justify-center gap-4 mb-6">
          <button
            onClick={() => navigate("/app")}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors flex items-center justify-center"
            title="Home"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </button>
          <button
            onClick={() => navigate("/app/all-trades")}
            className="px-6 py-2 text-sm font-medium text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
          >
            All Trades
          </button>
          <button
            onClick={() => navigate("/app/analytics")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Analytics
          </button>
          <button
            onClick={() => navigate("/app/tools")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Tools
          </button>
          <button
            onClick={() => navigate("/app/strategies")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Strategies
          </button>
          <button
            onClick={() => navigate("/app/social-trade")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Social Trade
          </button>
          <button
            onClick={() => navigate("/app/community")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Community
          </button>
          {user && (
            <button
              onClick={() => navigate('/app/add-trade')}
              className="px-6 py-2 text-sm font-medium text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 hover:border-orange-600 transition-colors"
            >
              Add Trade
            </button>
          )}
        </section>
        </>
        )}

        <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <div className="w-full sm:max-w-xs">
              <input
                type="text"
                value={filterSymbol}
                onChange={(e) => setFilterSymbol(e.target.value)}
                placeholder={t('allTrades.searchSymbol')}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex-1 sm:flex-none px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {showFilters ? t('allTrades.hideFilters') : t('allTrades.showFilters')}
              </button>
              <button
                type="button"
                onClick={() => { setEditTableDraft([...visibleColumns]); setShowEditTableModal(true) }}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:ring-offset-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {t('allTrades.editTable')}
              </button>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOnlyOpen}
                  onChange={(e) => setShowOnlyOpen(e.target.checked)}
                  className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
                />
                <span className="text-xs md:text-sm text-gray-700">{t('tradeList.showOnlyOpen')}</span>
              </label>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={() => setShowImport(true)}
                  className="flex-1 sm:flex-none px-3 py-2 text-xs md:text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  title="Import trades"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="hidden sm:inline">Import</span>
                  <span className="sm:hidden text-[10px]">Import</span>
                </button>
                <button
                  onClick={exportToExcel}
                  disabled={filteredTrades.length === 0}
                  className="flex-1 sm:flex-none px-3 py-2 text-xs md:text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:border-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  title="Export to Excel"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="hidden sm:inline">Excel</span>
                  <span className="sm:hidden text-[10px]">Excel</span>
                </button>
              </div>
            </div>
          </div>
          
          <div className="mb-4 sm:hidden flex justify-between items-center text-xs text-gray-500 bg-gray-50 p-2 rounded-lg">
            <span>Filtered: <strong>{filteredTrades.length}</strong></span>
            <span>Total: <strong>{trades.length}</strong></span>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-in fade-in slide-in-from-top-4 duration-200">
              {/* Quick date buttons */}
              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wider">Entry date (quick)</label>
                <div className="flex flex-wrap gap-2">
                  {(['all', 'd', 'w', 'm', '6m', 'ytd', 'Y'] as const).map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setFilterQuickDate(k)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        filterQuickDate === k
                          ? 'bg-orange-500 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {k === 'all' ? t('allTrades.quickDateAll') : k === 'd' ? t('allTrades.quickDateD') : k === 'w' ? t('allTrades.quickDateW') : k === 'm' ? t('allTrades.quickDateM') : k === '6m' ? t('allTrades.quickDate6m') : k === 'ytd' ? t('allTrades.quickDateYtd') : t('allTrades.quickDateY')}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* Market Filter */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Market</label>
                  <select
                    value={filterMarket}
                    onChange={(e) => setFilterMarket(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                  >
                    <option value="all">All Markets</option>
                    <option value="Commodity">Commodity</option>
                    <option value="Crypto">Crypto</option>
                    <option value="Forex">Forex</option>
                    <option value="Stock">Stock</option>
                  </select>
                </div>

                {/* Exchange Filter */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Exchange</label>
                  <select
                    value={filterExchange}
                    onChange={(e) => setFilterExchange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                    disabled={availableExchanges.length === 0}
                    title={availableExchanges.length === 0 ? 'No exchange data available' : 'Filter by exchange'}
                  >
                    <option value="all">All Exchanges</option>
                    {availableExchanges.map((ex) => (
                      <option key={ex} value={ex}>
                        {ex}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Account Filter */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Account</label>
                  <select
                    value={filterAccount}
                    onChange={(e) => setFilterAccount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                  >
                    <option value="all">All Accounts</option>
                    <option value="__none__">No Account</option>
                    {accounts.map((acc) => (
                      <option key={acc.id} value={acc.id}>
                        {acc.platform} ({acc.currency})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Long/Short Filter */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Direction</label>
                  <select
                    value={filterLongShort}
                    onChange={(e) => setFilterLongShort(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                  >
                    <option value="all">All</option>
                    <option value="long">Long</option>
                    <option value="short">Short</option>
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Status</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                  >
                    <option value="all">All</option>
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                {/* Strategy Filter */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Strategy</label>
                  <select
                    value={filterStrategy}
                    onChange={(e) => setFilterStrategy(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                  >
                    <option value="all">All Strategies</option>
                    <option value="__none__">No Strategy</option>
                    {strategies.map((strategy) => (
                      <option key={strategy.id} value={strategy.id}>
                        {strategy.display_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Time Interval Filter */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Timeframe</label>
                  <select
                    value={filterTimeInterval}
                    onChange={(e) => setFilterTimeInterval(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                  >
                    <option value="all">All Intervals</option>
                    <option value="__none__">No Timeframe</option>
                    <option value="1m">1m</option>
                    <option value="5m">5m</option>
                    <option value="15m">15m</option>
                    <option value="30m">30m</option>
                    <option value="1h">1h</option>
                    <option value="4h">4h</option>
                    <option value="d">d</option>
                    <option value="w">w</option>
                    <option value="m">m</option>
                  </select>
                </div>

                {/* Entry date range */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Entry from</label>
                  <input
                    type="date"
                    value={filterEntryDateFrom}
                    onChange={(e) => setFilterEntryDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Entry to</label>
                  <input
                    type="date"
                    value={filterEntryDateTo}
                    onChange={(e) => setFilterEntryDateTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                  />
                </div>
                {/* Exit date range */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Exit from</label>
                  <input
                    type="date"
                    value={filterExitDateFrom}
                    onChange={(e) => setFilterExitDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Exit to</label>
                  <input
                    type="date"
                    value={filterExitDateTo}
                    onChange={(e) => setFilterExitDateTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                  />
                </div>
              </div>

              {/* Advanced Filters Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* RR Filter Section */}
                <div className="p-3 bg-white rounded-lg border border-gray-200">
                  <label className="block text-xs font-bold text-orange-700 mb-2 uppercase tracking-wider">RR (Risk/Reward)</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div className="col-span-2 sm:col-span-1">
                      <select
                        value={filterRrType}
                        onChange={(e) => {
                          setFilterRrType(e.target.value as 'none' | 'greater' | 'less' | 'between')
                          if (e.target.value === 'none') {
                            setFilterRrValue('')
                            setFilterRrValue2('')
                          }
                        }}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500 text-xs"
                      >
                        <option value="none">No Filter</option>
                        <option value="greater">Min</option>
                        <option value="less">Max</option>
                        <option value="between">Range</option>
                      </select>
                    </div>
                    <div>
                      <input
                        type="number"
                        step="0.1"
                        value={filterRrValue}
                        onChange={(e) => setFilterRrValue(e.target.value)}
                        placeholder="Val"
                        disabled={filterRrType === 'none'}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500 text-xs disabled:bg-gray-100"
                      />
                    </div>
                    {filterRrType === 'between' && (
                      <div>
                        <input
                          type="number"
                          step="0.1"
                          value={filterRrValue2}
                          onChange={(e) => setFilterRrValue2(e.target.value)}
                          placeholder="Max"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500 text-xs"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* P&L % Filter Section */}
                <div className="p-3 bg-white rounded-lg border border-gray-200">
                  <label className="block text-xs font-bold text-green-700 mb-2 uppercase tracking-wider">P&L %</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div className="col-span-2 sm:col-span-1">
                      <select
                        value={filterPnlPercentType}
                        onChange={(e) => {
                          setFilterPnlPercentType(e.target.value as 'none' | 'greater' | 'less' | 'between')
                          if (e.target.value === 'none') {
                            setFilterPnlPercentValue('')
                            setFilterPnlPercentValue2('')
                          }
                        }}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500 text-xs"
                      >
                        <option value="none">No Filter</option>
                        <option value="greater">Min %</option>
                        <option value="less">Max %</option>
                        <option value="between">Range %</option>
                      </select>
                    </div>
                    <div>
                      <input
                        type="number"
                        step="0.5"
                        value={filterPnlPercentValue}
                        onChange={(e) => setFilterPnlPercentValue(e.target.value)}
                        placeholder="%"
                        disabled={filterPnlPercentType === 'none'}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500 text-xs disabled:bg-gray-100"
                      />
                    </div>
                    {filterPnlPercentType === 'between' && (
                      <div>
                        <input
                          type="number"
                          step="0.5"
                          value={filterPnlPercentValue2}
                          onChange={(e) => setFilterPnlPercentValue2(e.target.value)}
                          placeholder="Max %"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500 text-xs"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-xs md:text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Clear All
                </button>
                <button
                  onClick={() => setShowFilters(false)}
                  className="px-4 py-2 text-xs md:text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-gray-500 text-sm">Loading trades...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          ) : (
            <>
              {trades.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 mb-4">No trades found. Start by adding your first trade!</p>
                  <button
                    onClick={() => navigate('/app/add-trade')}
                    className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
                  >
                    Add Trade
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-4 md:mx-0">
                  <table className="min-w-full md:w-full border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th 
                          className="px-3 py-2 text-left text-sm font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => handleSort('symbol')}
                        >
                          {t('allTrades.columnSymbol')} {sortField === 'symbol' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        {visibleColumns.map((col) => {
                          const isPrice = col === 'entry_price' || col === 'exit_price' || col === 'average_cost' || col === 'sl' || col === 'tp'
                          const responsiveClass = col === 'entry_price' ? 'hidden md:table-cell' : (col === 'exit_date' || col === 'exit_price') ? 'hidden lg:table-cell' : ''
                          const alignClass = col === 'position_size' || col === 'total_invested' || col === 'pnl' || col === 'pnl_percent' || col === 'risk' || col === 'risk_percent' || col === 'rr' || col === 'fee' || isPrice ? 'text-right' : 'text-left'
                          const label = getTableColumnLabel(col)
                          return (
                            <th
                              key={col}
                              className={`px-2 py-2 text-sm font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors ${alignClass} ${responsiveClass}`}
                              onClick={() => handleSort(col)}
                            >
                              {label}{sortField === col && (sortDirection === 'asc' ? ' ↑' : ' ↓')}
                            </th>
                          )
                        })}
                        <th className="px-2 py-2 text-center text-sm font-bold text-gray-600 uppercase tracking-wider">
                          {t('allTrades.columnStatus')}
                        </th>
                        <th className="px-3 py-2 text-center text-sm font-bold text-gray-600 uppercase tracking-wider">
                          {t('allTrades.columnAction')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-app-box divide-y divide-gray-100">
                      {filteredTrades.map((trade) => {
                        const isOpen = isTradeOpen(trade)
                        const formatShortDate = (dateString: string | null) => {
                          if (!dateString) return '-'
                          const date = new Date(dateString)
                          return date.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: date.getFullYear() !== new Date().getFullYear() ? '2-digit' : undefined
                          })
                        }
                        return (
                          <tr 
                            key={trade.id} 
                            className="hover:bg-orange-50/30 transition-colors cursor-pointer group"
                            onClick={() => handleNavigateToDetail(trade)}
                          >
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-sm text-gray-900 group-hover:text-orange-600 transition-colors">{trade.symbol}</span>
                                <span
                                  className={`px-2 py-0.5 rounded text-xs font-bold shrink-0 ${
                                    trade.long_short === 'long'
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}
                                >
                                  {trade.long_short.toUpperCase()}
                                </span>
                              </div>
                            </td>
                            {visibleColumns.map((col) => {
                              const isPrice = col === 'entry_price' || col === 'exit_price' || col === 'average_cost' || col === 'sl' || col === 'tp'
                              const responsiveClass = col === 'entry_price' ? 'hidden md:table-cell' : (col === 'exit_date' || col === 'exit_price') ? 'hidden lg:table-cell' : ''
                              const alignRight = col === 'position_size' || col === 'total_invested' || col === 'pnl' || col === 'pnl_percent' || col === 'risk' || col === 'risk_percent' || col === 'rr' || col === 'fee' || isPrice
                              const renderCell = () => {
                                if (col === 'market') return trade.market ?? '-'
                                if (col === 'exchange') return trade.exchange ?? '-'
                                if (col === 'entry_date') return formatShortDate(trade.entry_date)
                                if (col === 'entry_price') return formatPriceWithCurrency(trade.entry_price, trade.currency)
                                if (col === 'exit_date') return isOpen ? '-' : formatShortDate(trade.exit_date)
                                if (col === 'exit_price') return isOpen ? '-' : formatPriceWithCurrency(trade.exit_price, trade.currency)
                                if (col === 'position_size') return trade.position_size != null ? formatCurrency(trade.position_size, trade.currency) : '-'
                                if (col === 'total_invested') {
                                  const invested = getTotalInvested(trade)
                                  return invested != null ? formatCurrency(invested, trade.currency) : '-'
                                }
                                if (col === 'average_cost') {
                                  const avg = getAverageCost(trade)
                                  return avg != null ? formatPriceWithCurrency(avg, trade.currency) : '-'
                                }
                                if (col === 'rr') return trade.rr != null ? trade.rr.toFixed(2).replace('.', ',') : '-'
                                if (col === 'sl') return formatPriceWithCurrency(trade.stop_loss, trade.currency)
                                if (col === 'tp') return formatPriceWithCurrency(trade.take_profit, trade.currency)
                                if (col === 'risk') {
                                  const riskVal = getPositionRisk(trade)
                                  return riskVal != null ? formatCurrency(riskVal, trade.currency) : '-'
                                }
                                if (col === 'risk_percent') {
                                  const riskPct = getPositionRiskPercent(trade)
                                  if (riskPct == null) return <span className="text-gray-400">-</span>
                                  return `${riskPct.toFixed(2).replace('.', ',')}%`
                                }
                                if (col === 'pnl') {
                                  const val = isOpen ? trade.unrealized_pnl : trade.pnl
                                  if (val == null) return <span className="text-gray-400">-</span>
                                  return (
                                    <span className={val >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                      {formatCurrency(val, trade.currency)}
                                    </span>
                                  )
                                }
                                if (col === 'pnl_percent') {
                                  const pct = isOpen ? trade.unrealized_pnl_percentage : trade.pnl_percentage
                                  if (pct == null) return <span className="text-gray-400">-</span>
                                  return (
                                    <span className={pct >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                      {pct >= 0 ? '+' : ''}{pct.toFixed(2).replace('.', ',')}%
                                    </span>
                                  )
                                }
                                if (col === 'fee') return trade.fee != null ? formatCurrency(trade.fee, trade.currency) : '-'
                                if (col === 'tags') return Array.isArray(trade.tags) && trade.tags.length > 0 ? trade.tags.join(', ') : '-'
                                if (col === 'account') {
                                  const acc = trade.account_id ? accounts.find(a => a.id === trade.account_id) : null
                                  return acc ? `${acc.platform} (${acc.currency})` : '-'
                                }
                                return null
                              }
                              return (
                                <td
                                  key={col}
                                  className={`px-2 py-2 whitespace-nowrap text-sm ${alignRight ? 'text-right' : 'text-gray-600'} ${(col === 'entry_price' || col === 'average_cost') ? 'font-medium text-gray-900' : ''} ${responsiveClass}`}
                                >
                                  {renderCell()}
                                </td>
                              )
                            })}
                            <td className="px-2 py-2 whitespace-nowrap text-center">
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-bold ${
                                  isOpen
                                    ? 'bg-blue-50 text-blue-600'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {isOpen ? 'OPEN' : 'CLOSED'}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setEditingTrade(trade)
                                  }}
                                  className="p-1.5 text-orange-600 hover:bg-orange-100 rounded-full transition-colors"
                                  title="Quick edit"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    navigate(`/app/edit-trade/${trade.id}`)
                                  }}
                                  className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                                  title="Full edit"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </button>
                                {!trade.is_public && (
                                <button
                                  onClick={(e) => handleDelete(trade.id, e)}
                                  disabled={deletingId === trade.id}
                                  className="p-1.5 text-red-600 hover:bg-red-100 rounded-full transition-colors disabled:opacity-50"
                                  title="Delete"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h14" />
                                  </svg>
                                </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                  
                  {filteredTrades.length === 0 && (
                    <div className="text-center py-12">
                      <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-gray-500 text-sm">No trades match your filters.</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        </W>
        );
      })()}

      <Footer currentPage={undefined} onNavigate={(page) => {
        if (page === "privacy-policy") navigate("/privacy");
        else if (page === "terms-and-conditions") navigate("/terms");
        else if (page === "risk-warning") navigate("/risk-warning");
        else if (page === "documentation") navigate("/documentation");
        else if (page === "contact") navigate("/contact");
        else if (page === "home") onBack();
        else if (page === "changelog") setCurrentPage("changelog");
      }} />

      {/* Modals */}
      {showImport && (
        <ImportTrades
          onClose={() => setShowImport(false)}
          onSuccess={() => {
            setShowImport(false)
            setTradeListRefresh((prev) => prev + 1)
          }}
        />
      )}

      {editingTrade && (
        <QuickAddTradeModal
          key={editingTrade.id}
          trade={editingTrade}
          onClose={() => setEditingTrade(null)}
          onSuccess={() => {
            setEditingTrade(null)
            setTradeListRefresh((prev) => prev + 1)
          }}
        />
      )}

      {/* Edit table columns modal (like Trade Journal) */}
      {showEditTableModal && editTableDraft !== null && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200] p-4"
          onClick={() => { setShowEditTableModal(false); setEditTableDraft(null) }}
        >
          <div
            className="rounded-2xl max-w-sm w-full p-6 shadow-xl bg-white border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('allTrades.editTableTitle')}</h3>
            <p className="text-sm text-gray-500 mb-4">{t('allTrades.selectColumns')}</p>
            <div className="space-y-0.5 mb-6">
              {ALL_TABLE_COLUMNS.map((id) => {
                const label = getTableColumnLabel(id)
                const checked = editTableDraft.includes(id)
                return (
                  <label key={id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded py-1 px-2">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setEditTableDraft((prev) => prev == null ? prev : checked ? prev.filter((c) => c !== id) : [...prev, id])
                      }}
                      className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                )
              })}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setShowEditTableModal(false); setEditTableDraft(null) }}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                {t('buttons.cancel', { ns: 'common' })}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (editTableDraft.length > 0) {
                    setVisibleColumns(editTableDraft)
                    try { localStorage.setItem(TABLE_STORAGE_KEY, JSON.stringify(editTableDraft)) } catch (_) {}
                  }
                  setShowEditTableModal(false)
                  setEditTableDraft(null)
                }}
                disabled={editTableDraft.length === 0}
                className="px-3 py-1.5 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('buttons.save', { ns: 'common' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {!embedded && showLogin && (
        <Login
          onClose={() => setShowLogin(false)}
          onSwitchToRegister={() => {
            setShowLogin(false)
            setShowRegister(true)
          }}
        />
      )}

      {!embedded && showRegister && (
        <Register
          onClose={() => setShowRegister(false)}
          onSwitchToLogin={() => {
            setShowRegister(false)
            setShowLogin(true)
          }}
        />
      )}
    </div>
  )
}

export default AllTrades

